import { useEffect, useMemo, useState } from 'react';
import {
  Badge, Button, Callout, Card, NumberInput, Select, SelectItem, Tab, TabGroup,
  TabList, TabPanel, TabPanels, Text, TextInput, Title,
} from '@tremor/react';
import {
  getCashboxStatus, getCashboxDenoms, openCashbox, cashboxDrop, closeCashbox,
} from '../api/cashbox';
import { canCashboxOperate, canCashboxManage } from '../lib/roles';
import { exportReportPdf } from '../lib/pdf';
import { t } from '../lib/i18n';
import type { CashboxStatus, CashboxSession, Denom, User } from '../api/types';

const fmtMxn = (n: number) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2 }).format(n);

const sessionKey = (s: { sid: number; cash_box: number; close_code: number }) =>
  `${s.sid}-${s.cash_box}-${s.close_code}`;

/** Blind denomination count grid. counts is indexed 1..20 (0 unused). */
function DenomGrid({
  denoms, counts, onChange,
}: {
  denoms: Denom[];
  counts: number[];
  onChange: (idx: number, n: number) => void;
}) {
  const c = t.caja.sesiones;
  const total = denoms.reduce((sum, d) => sum + (counts[d.idx] || 0) * d.mult, 0);
  return (
    <div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-tremor-content dark:text-dark-tremor-content">
            <th className="py-1">{c.denom}</th>
            <th className="py-1 text-right">{c.count}</th>
            <th className="py-1 text-right">{c.subtotal}</th>
          </tr>
        </thead>
        <tbody>
          {denoms.map((d) => (
            <tr key={d.idx}>
              <td className="py-1">{d.label} <span className="opacity-60">× {d.mult}</span></td>
              <td className="py-1 w-28">
                <NumberInput
                  value={counts[d.idx] || 0}
                  min={0}
                  onValueChange={(n) => onChange(d.idx, Number.isFinite(n) ? n : 0)}
                  enableStepper={false}
                />
              </td>
              <td className="py-1 text-right tabular-nums">{fmtMxn((counts[d.idx] || 0) * d.mult)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-2 flex justify-between font-semibold">
        <span>{c.total}</span>
        <span className="tabular-nums">{fmtMxn(total)}</span>
      </div>
    </div>
  );
}

export default function CajaSessions({ user }: { user: User }) {
  const c = t.caja.sesiones;
  const canOperate = canCashboxOperate(user);
  const canManage = canCashboxManage(user);

  const [status, setStatus] = useState<CashboxStatus | null>(null);
  const [denoms, setDenoms] = useState<Denom[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // Abrir form.
  const [openDrawer, setOpenDrawer] = useState<string>('');
  const [openCounts, setOpenCounts] = useState<number[]>([]);
  // Retiro form.
  const [dropSession, setDropSession] = useState<string>('');
  const [dropAmount, setDropAmount] = useState<number>(0);
  const [dropKind, setDropKind] = useState<'drop' | 'add'>('drop');
  const [dropMemo, setDropMemo] = useState<string>('');
  // Cerrar form.
  const [closeSession, setCloseSession] = useState<string>('');
  const [closeCounts, setCloseCounts] = useState<number[]>([]);
  const [lastImbalance, setLastImbalance] = useState<number | null>(null);
  // Conciliar form.
  const [complSession, setComplSession] = useState<string>('');
  const [adjust, setAdjust] = useState<number>(0);

  async function refresh() {
    try {
      const s = await getCashboxStatus();
      setStatus(s);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  useEffect(() => {
    refresh();
    getCashboxDenoms().then((r) => setDenoms(r.denoms || [])).catch(() => setDenoms([]));
  }, []);

  const openSessions = status?.open_sessions ?? [];
  const incomplete = status?.incomplete_sessions ?? [];
  const drawers = status?.drawers ?? [];
  const closableDrawers = useMemo(() => drawers.filter((d) => !d.open_flag), [drawers]);

  function findDrawer(key: string) {
    return drawers.find((d) => `${d.sid}-${d.cash_box}` === key);
  }
  function findSession(list: CashboxSession[], key: string) {
    return list.find((s) => sessionKey(s) === key);
  }

  async function doOpen() {
    const d = findDrawer(openDrawer);
    if (!d) { setErr(c.pickDrawer); return; }
    setBusy(true); setErr(null); setToast(null);
    try {
      const breakout = Array.from({ length: 20 }, (_, i) => openCounts[i + 1] || 0);
      const r = await openCashbox({ sid: d.sid, cash_box: d.cash_box, description: d.description, open_breakout: breakout });
      if (!r.ok) throw new Error(r.error || 'error');
      setToast(c.opened); setOpenCounts([]); setOpenDrawer('');
      await refresh();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }

  async function doDrop() {
    const s = findSession(openSessions, dropSession);
    if (!s) { setErr(c.noOpen); return; }
    setBusy(true); setErr(null); setToast(null);
    try {
      const r = await cashboxDrop({
        sid: s.sid, cash_box: s.cash_box, close_code: s.close_code,
        cash_code: '', amount: dropAmount, rate: 1, kind: dropKind, is_cash: 1,
      });
      if (!r.ok) throw new Error(r.error || 'error');
      setToast(c.dropped); setDropAmount(0); setDropMemo('');
      await refresh();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }

  async function doClose() {
    const s = findSession(openSessions, closeSession);
    if (!s) { setErr(c.noOpen); return; }
    setBusy(true); setErr(null); setToast(null);
    try {
      const breakout = Array.from({ length: 20 }, (_, i) => closeCounts[i + 1] || 0);
      const r = await closeCashbox({
        sid: s.sid, cash_box: s.cash_box, close_code: s.close_code,
        close_breakout: breakout, tender_counts: [], complete: false,
      });
      if (!r.ok) throw new Error(r.error || 'error');
      setLastImbalance(r.data?.imbalance ?? null);
      setToast(c.closed); setCloseCounts([]); setCloseSession('');
      await refresh();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }

  async function doComplete() {
    const s = findSession(incomplete, complSession);
    if (!s) { setErr(c.noOpen); return; }
    setBusy(true); setErr(null); setToast(null);
    try {
      const r = await closeCashbox({
        sid: s.sid, cash_box: s.cash_box, close_code: s.close_code,
        close_breakout: [], tender_counts: [], complete: true, adjust_bal: adjust,
      });
      if (!r.ok) throw new Error(r.error || 'error');
      setToast(c.completed); setAdjust(0); setComplSession('');
      await refresh();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }

  function printCorte() {
    exportReportPdf({
      title: c.corte,
      printedBy: user.username,
      columns: [c.drawer, c.sessionNum, c.openBy, c.since],
      rows: openSessions.map((s) => [String(s.cash_box), String(s.close_code), String(s.operator ?? ''), s.open_time]),
      filename: 'corte-caja.pdf',
    });
  }

  return (
    <div className="space-y-4">
      {!user.features?.writes_enabled && (
        <Callout title={c.readonly} color="gray">{c.registerStops}</Callout>
      )}
      {err && <Callout title="Error" color="rose">{err}</Callout>}
      {toast && <Callout title={toast} color="emerald" />}

      <TabGroup>
        <TabList variant="solid">
          <Tab>{c.estado}</Tab>
          <Tab>{c.abrir}</Tab>
          <Tab>{c.retiro}</Tab>
          <Tab>{c.cerrarContar}</Tab>
          <Tab>{c.conciliar}</Tab>
        </TabList>
        <TabPanels>
          {/* Estado */}
          <TabPanel>
            <div className="mt-3 grid gap-3 md:grid-cols-2">
              <Card>
                <Title>{c.drawer}</Title>
                {drawers.map((d) => (
                  <div key={`${d.sid}-${d.cash_box}`} className="flex justify-between border-b py-1 text-sm">
                    <span>#{d.cash_box} {d.description}</span>
                    <span>{d.open_flag ? <Badge color="emerald">{c.abrir}</Badge> : <Badge color="gray">—</Badge>} {fmtMxn(d.curr_bal)}</span>
                  </div>
                ))}
              </Card>
              <Card>
                <Title>{c.estado}</Title>
                {openSessions.length === 0 && <Text>{c.noOpen}</Text>}
                {openSessions.map((s) => (
                  <div key={sessionKey(s)} className="border-b py-1 text-sm">
                    #{s.cash_box} · {c.sessionNum} {s.close_code} · {c.float} {fmtMxn(s.open_bal ?? 0)} · {s.open_time}
                  </div>
                ))}
                {incomplete.length > 0 && (
                  <div className="mt-2">
                    <Text className="font-semibold">{c.conciliar}</Text>
                    {incomplete.map((s) => (
                      <div key={sessionKey(s)} className="text-sm">#{s.cash_box} · {c.sessionNum} {s.close_code} (status {s.box_status})</div>
                    ))}
                  </div>
                )}
                <Button size="xs" variant="secondary" className="mt-2" onClick={printCorte}>{c.printCorte}</Button>
              </Card>
            </div>
          </TabPanel>

          {/* Abrir */}
          <TabPanel>
            <Card className="mt-3">
              <Title>{c.abrir}</Title>
              <div className="mt-2 max-w-xs">
                <Select value={openDrawer} onValueChange={setOpenDrawer} placeholder={c.pickDrawer} disabled={!canOperate}>
                  {closableDrawers.map((d) => (
                    <SelectItem key={`${d.sid}-${d.cash_box}`} value={`${d.sid}-${d.cash_box}`}>
                      #{d.cash_box} {d.description}
                    </SelectItem>
                  ))}
                </Select>
              </div>
              <div className="mt-3 max-w-md">
                <DenomGrid denoms={denoms} counts={openCounts} onChange={(i, n) => setOpenCounts((p) => { const q = [...p]; q[i] = n; return q; })} />
              </div>
              <Button className="mt-3" disabled={!canOperate || busy || !openDrawer} onClick={doOpen}>{c.openBtn}</Button>
            </Card>
          </TabPanel>

          {/* Retiro */}
          <TabPanel>
            <Card className="mt-3 max-w-md">
              <Title>{c.retiro}</Title>
              <div className="mt-2 space-y-2">
                <Select value={dropSession} onValueChange={setDropSession} placeholder={c.noOpen} disabled={!canOperate}>
                  {openSessions.map((s) => (
                    <SelectItem key={sessionKey(s)} value={sessionKey(s)}>#{s.cash_box} · {c.sessionNum} {s.close_code}</SelectItem>
                  ))}
                </Select>
                <Select value={dropKind} onValueChange={(v) => setDropKind(v as 'drop' | 'add')} disabled={!canOperate}>
                  <SelectItem value="drop">{c.drop}</SelectItem>
                  <SelectItem value="add">{c.add}</SelectItem>
                </Select>
                <NumberInput value={dropAmount} min={0} onValueChange={(n) => setDropAmount(Number.isFinite(n) ? n : 0)} placeholder={c.dropAmount} disabled={!canOperate} />
                <TextInput value={dropMemo} onValueChange={setDropMemo} placeholder={c.memo} disabled={!canOperate} />
                <Button disabled={!canOperate || busy || !dropSession || dropAmount <= 0} onClick={doDrop}>{c.dropBtn}</Button>
              </div>
            </Card>
          </TabPanel>

          {/* Cerrar / Contar */}
          <TabPanel>
            <Card className="mt-3 max-w-md">
              <Title>{c.cerrarContar}</Title>
              <Text className="text-xs">{c.registerStops}</Text>
              <div className="mt-2 space-y-2">
                <Select value={closeSession} onValueChange={setCloseSession} placeholder={c.noOpen} disabled={!canOperate}>
                  {openSessions.map((s) => (
                    <SelectItem key={sessionKey(s)} value={sessionKey(s)}>#{s.cash_box} · {c.sessionNum} {s.close_code}</SelectItem>
                  ))}
                </Select>
                <DenomGrid denoms={denoms} counts={closeCounts} onChange={(i, n) => setCloseCounts((p) => { const q = [...p]; q[i] = n; return q; })} />
                <Button disabled={!canOperate || busy || !closeSession} onClick={doClose}>{c.closeBtn}</Button>
                {lastImbalance !== null && (
                  <Callout title={`${c.overShort}: ${fmtMxn(lastImbalance)}`} color={Math.abs(lastImbalance) < 0.01 ? 'emerald' : 'amber'} />
                )}
              </div>
            </Card>
          </TabPanel>

          {/* Conciliar / Completar */}
          <TabPanel>
            <Card className="mt-3 max-w-md">
              <Title>{c.conciliar}</Title>
              {!canManage && <Text className="text-xs">{c.readonly}</Text>}
              <div className="mt-2 space-y-2">
                <Select value={complSession} onValueChange={setComplSession} placeholder={c.noOpen} disabled={!canManage}>
                  {incomplete.map((s) => (
                    <SelectItem key={sessionKey(s)} value={sessionKey(s)}>#{s.cash_box} · {c.sessionNum} {s.close_code}</SelectItem>
                  ))}
                </Select>
                <NumberInput value={adjust} onValueChange={(n) => setAdjust(Number.isFinite(n) ? n : 0)} placeholder={c.adjust} disabled={!canManage} />
                <Button disabled={!canManage || busy || !complSession} onClick={doComplete}>{c.completeBtn}</Button>
              </div>
            </Card>
          </TabPanel>
        </TabPanels>
      </TabGroup>
    </div>
  );
}
